import { useLayoutEffect, useState, useCallback, useRef } from 'react'
import { TPosition } from 'types'
export type TAlignmentX = 'left' | 'right'
export type TAlignmentY = 'top' | 'bottom'

export interface IOptionsType {
  position: TPosition
  alignmentX?: TAlignmentX
  alignmentY?: TAlignmentY
  extraOffset?: number
}

const DEFAULT_OPTIONS: IOptionsType = {
  position: 'bottom',
  alignmentX: 'left',
  alignmentY: 'top',
  extraOffset: 0
}

const DEFAULT_COORDS = {
  top: 0,
  left: 0
}

const POSITIONS = ['bottom', 'top', 'right', 'left']

const calculateHorizontalAlignment = ({
  anchorLeft,
  anchorWidth,
  targetWidth,
  alignment
}: {
  anchorLeft: number
  anchorWidth: number
  targetWidth: number
  alignment: TAlignmentX
}): number => {
  if (alignment === 'left') {
    return anchorLeft
  }
  if (alignment === 'right') {
    return anchorLeft + anchorWidth - targetWidth
  }
  // in case of center alignment
  return anchorLeft + (anchorWidth - targetWidth) / 2
}

const calculateVerticalAlignment = ({
  anchorTop,
  anchorHeight,
  targetHeight,
  alignment
}: {
  anchorTop: number
  anchorHeight: number
  targetHeight: number
  alignment: TAlignmentY
}): number => {
  const extraOffset = 10
  const topAlignment = anchorTop
  const bottomAlignment = anchorTop + anchorHeight - targetHeight
  const centerAlignment = anchorTop + (anchorHeight - targetHeight) / 2

  const topDistanceToContainer =
    topAlignment + targetHeight + extraOffset - window.innerHeight
  const bottomDistanceToContainer = bottomAlignment

  const isTopPositionInsideContainer = topDistanceToContainer < 0
  const isBottomPositionInsideContainer = bottomDistanceToContainer > 0
  if (alignment === 'top') {
    return isTopPositionInsideContainer
      ? topAlignment
      : topAlignment - topDistanceToContainer
  }
  if (alignment === 'bottom') {
    return isBottomPositionInsideContainer
      ? bottomAlignment
      : bottomAlignment - bottomDistanceToContainer
  }
  // in case of center alignment
  return centerAlignment
}

const checkPositionAvailability = ({
  position,
  targetWidth,
  targetHeight,
  anchorRect,
  containerRect,
  extraOffset
}: {
  position: TPosition
  targetWidth: number
  targetHeight: number
  anchorRect: DOMRect
  containerRect: DOMRect
  extraOffset: number
}): boolean => {
  const {
    top: containerTop,
    left: containerLeft,
    width: containerWidth,
    height: containerHeight
  } = containerRect

  const {
    top: anchorTop,
    left: anchorLeft,
    height: anchorHeight,
    width: anchorWidth
  } = anchorRect

  switch (position) {
    case 'top':
      return anchorTop - targetHeight - extraOffset >= containerTop
    case 'bottom':
      return (
        anchorTop + anchorHeight + targetHeight + extraOffset <=
        containerTop + containerHeight
      )
    case 'left':
      return anchorLeft - targetWidth - extraOffset >= containerLeft
    case 'right':
      return (
        anchorLeft + anchorWidth + targetWidth + extraOffset <=
        containerLeft + containerWidth
      )
    default:
      return false
  }
}

const findBestPosition = ({
  targetWidth,
  targetHeight,
  anchorRect,
  containerRect,
  initialPosition,
  extraOffset
}: {
  targetWidth: number
  targetHeight: number
  anchorRect: DOMRect
  containerRect: DOMRect
  initialPosition: TPosition
  extraOffset: number
}): TPosition => {
  // first try the initial position
  if (
    checkPositionAvailability({
      position: initialPosition,
      targetWidth,
      targetHeight,
      anchorRect,
      containerRect,
      extraOffset
    })
  ) {
    return initialPosition
  }

  // try other positions
  const remainingPositions = POSITIONS.filter(
    position => position !== initialPosition
  ) as TPosition[]

  for (const position of remainingPositions) {
    if (
      checkPositionAvailability({
        position,
        targetWidth,
        targetHeight,
        anchorRect,
        containerRect,
        extraOffset
      })
    ) {
      return position
    }
  }
  // if no position is available, return the initial position no matter if it's not fitting in the container
  return initialPosition
}

export const useRelativeCoords = ({
  container = document.body,
  anchorEl,
  targetRef,
  options
}: {
  container?: Element | null
  anchorEl: Element | null
  targetRef: { current: Element | null }
  options: IOptionsType
}) => {
  const [coords, setCoords] = useState(DEFAULT_COORDS)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)
  const {
    position: initialPosition,
    alignmentX, //in case of top or bottom position
    alignmentY, //in case of left or right position
    extraOffset = 0 // extra space between anchor and target
  } = options || DEFAULT_OPTIONS

  const calculateCoords = useCallback(() => {
    const targetEl = targetRef.current
    if (!targetEl || !anchorEl) {
      return
    }

    const targetRect = targetEl.getBoundingClientRect()
    const anchorRect = anchorEl.getBoundingClientRect()
    const containerRect = container?.getBoundingClientRect() || new DOMRect()
    const containerScrollTop = container?.scrollTop || 0

    const anchorTopBorderOffset =
      containerScrollTop + anchorRect.top - containerRect.top
    const anchorLeftBorderOffset = anchorRect.left - containerRect.left

    const bestPosition = findBestPosition({
      targetWidth: targetRect.width,
      targetHeight: targetRect.height,
      anchorRect,
      containerRect,
      initialPosition,
      extraOffset
    })

    let top = 0
    let left = 0

    const leftForHorizontalAlignment = calculateHorizontalAlignment({
      anchorLeft: anchorLeftBorderOffset,
      anchorWidth: anchorRect.width,
      targetWidth: targetRect.width,
      alignment: alignmentX as TAlignmentX
    })

    const topForVerticalAlignment = calculateVerticalAlignment({
      anchorTop: anchorTopBorderOffset,
      anchorHeight: anchorRect.height,
      targetHeight: targetRect.height,
      alignment: alignmentY as TAlignmentY
    })

    switch (bestPosition) {
      case 'top':
        top = anchorTopBorderOffset - targetRect.height - extraOffset
        left = leftForHorizontalAlignment
        break
      case 'bottom':
        top = anchorTopBorderOffset + anchorRect.height + extraOffset
        left = leftForHorizontalAlignment
        break
      case 'left':
        left = anchorLeftBorderOffset - targetRect.width - extraOffset
        top = topForVerticalAlignment
        break
      case 'right':
        left = anchorLeftBorderOffset + anchorRect.width + extraOffset
        top = topForVerticalAlignment
        break
    }

    setCoords({ top, left })
  }, [
    anchorEl,
    targetRef,
    container,
    alignmentX,
    alignmentY,
    extraOffset,
    initialPosition
  ])

  useLayoutEffect(() => {
    calculateCoords()

    // Set up ResizeObserver to detect target element size changes
    const targetEl = targetRef.current
    if (targetEl && 'ResizeObserver' in window) {
      resizeObserverRef.current = new ResizeObserver(() => {
        calculateCoords()
      })
      resizeObserverRef.current.observe(targetEl)
    }

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect()
        resizeObserverRef.current = null
      }
    }
  }, [calculateCoords, targetRef, anchorEl, container])

  return coords
}
